import { defineComponent as _defineComponent } from 'vue'
import {
  computed,
  nextTick,
  onMounted,
  onBeforeUnmount,
  ref,
  watch,
  withDefaults,
} from 'vue'
import type Quill from 'quill'
import type { Delta, EmitterSource } from 'quill'
import { twMerge } from 'tailwind-merge'

type TextChangeHandler = (
  delta: Delta,
  oldContent: Delta,
  source: EmitterSource,
) => void

interface RichTextEditorProps {
  content: string
  theme?: 'outline' | 'flat'
  twClass?: string | string[]
}


export default /*#__PURE__*/_defineComponent({
  __name: 'RichTextEditor',
  props: {
    content: null,
    theme: { default: 'outline' },
    twClass: { default: undefined }
  },
  emits: ['update:content'],
  setup(__props: any, { emit }) {

const props = __props as { content: string, theme: 'outline' | 'flat', twClass: string | string[] };





const editorStyle = computed(() =>
  props.theme === 'outline' ? 'quill-editor-outlined' : 'quill-editor-flat',
)
const toolbarStyle = computed(() =>
  props.theme === 'outline' ? 'quill-toolbar-outlined' : 'quill-toolbar-flat',
)

let quill: Quill | null
const editor = ref<HTMLElement>()
// Does not need reactivity
let internalContent = ''
const toolbarOptions = [
  [{ header: [1, 2, false] }],
  ['bold', 'italic', 'underline', 'strike'],
  [{ list: 'ordered' }, { list: 'bullet' }, { align: [] }],
  ['link'],
]

onMounted(() => {
  import('quill').then((module) => {
    const Quill = module.default

    if (!editor.value) {
      return
    }

    quill = new Quill(editor.value, {
      modules: {
        clipboard: {},
        toolbar: toolbarOptions,
        keyboard: {
          bindings: {
            indent: {
              key: 'tab',
              format: ['list'],
              handler: function () {
                return false
              },
            },
          },
        },
      },
      formats: [
        'header',
        'bold',
        'italic',
        'underline',
        'strike',
        'list',
        'align',
        'link',
      ],
      theme: 'snow',
    })

    setContents(props.content)

    quill.on('text-change', handleTextChange)
  })
})

onBeforeUnmount(() => {
  quill = null
})

const setContents = (content: string) => {
  if (!quill) {
    return
  }
  // Remove the event listener to only trigger a text change event when the content
  // changes from the user input and not from converting the quill content.
  // Re-add the event listener after setting the content.
  quill.off('text-change', handleTextChange)

  const delta = quill.clipboard.convert({ html: content })
  quill.setContents(delta)

  internalContent = quill.root.innerHTML

  quill.on('text-change', handleTextChange)
}

const handleTextChange: TextChangeHandler = () => {
  if (!quill) {
    return
  }

  internalContent = quill.root.innerHTML
  if (props.content !== internalContent) {
    emit('update:content', internalContent)
  }
}

watch(
  () => props.content,
  (newContent) => {
    if (!quill || internalContent === newContent) {
      return
    }

    // Restore the selection and cursor position after updating the content
    const selection = quill.getSelection()
    if (selection) {
      nextTick(() => quill?.setSelection(selection))
    }
    setContents(newContent)
  },
)

return { __sfc: true,props, emit, editorStyle, toolbarStyle, quill, editor, internalContent, toolbarOptions, setContents, handleTextChange, twMerge }
}

})